import { formatDueDateDisplay } from '../../../../../utils/missionDates';
import { formatRelativeTime } from './utils/formatRelativeTime';
import { CalendarIcon, ClockIcon } from './icons';

/**
 * Field configurations for due/estimate/timestamp rows.
 * In template mode, certain fields are disabled or behave differently — see inline comments in original DueDateDisplay.
 */
export function buildDueDateFields({
  task,
  isTemplate,
  showDueDate,
  showEstimatedDuration,
  showCreatedAt,
  showStartedAt,
  showCompletedAt,
  showUpdatedAt,
  onDueDateChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
}) {
  const dueDateFormatted = formatDueDateDisplay(task.dueDate);

  return [
    {
      id: 'dueDate',
      show: showDueDate,
      label: 'Due:',
      value: task.dueDate,
      displayValue: dueDateFormatted?.text,
      placeholder: isTemplate ? 'N/A in templates' : 'Click to set',
      statusClass: dueDateFormatted?.status,
      handler: isTemplate ? null : onDueDateChange,
      inputType: 'datetime',
      icon: CalendarIcon,
      showTime: true,
      disabledInTemplate: isTemplate,
      templateTooltip:
        "Due dates can't be set in templates. When you create a campaign from this template, you can set due dates for each mission.",
    },
    {
      id: 'estimatedDuration',
      show: showEstimatedDuration,
      label: 'Est. time:',
      handler: onEstimatedDurationMinutesChange,
      inputType: 'duration',
    },
    {
      id: 'createdAt',
      show: showCreatedAt && (task.createdAt || !isTemplate),
      label: 'Created:',
      value: task.createdAt,
      displayValue: task.createdAt ? formatRelativeTime(task.createdAt) : null,
      placeholder: isTemplate ? 'Set on creation' : '—',
      handler: isTemplate ? null : onCreatedAtChange,
      inputType: 'datetime',
      icon: ClockIcon,
      showTime: true,
      disabledInTemplate: isTemplate,
      templateTooltip:
        'Created date is automatically set when a campaign is created from this template.',
    },
    {
      id: 'startedAt',
      show: showStartedAt,
      label: 'Started:',
      value: task.startedAt,
      displayValue: task.startedAt ? formatRelativeTime(task.startedAt) : null,
      placeholder: isTemplate ? 'Set on creation' : 'Not started',
      handler: isTemplate ? null : onStartedAtChange,
      inputType: 'datetime',
      icon: ClockIcon,
      showTime: true,
      disabledInTemplate: isTemplate,
      templateTooltip:
        'Started date is automatically set when a campaign is created from this template (if the mission is already in progress).',
    },
    {
      id: 'completedAt',
      show: showCompletedAt,
      label: 'Completed:',
      value: task.completedAt,
      displayValue: task.completedAt ? formatRelativeTime(task.completedAt) : null,
      placeholder: isTemplate ? 'Set on creation' : 'Not completed',
      handler: isTemplate ? null : onCompletedAtChange,
      inputType: 'datetime',
      icon: ClockIcon,
      showTime: true,
      disabledInTemplate: isTemplate,
      templateTooltip:
        'Completed date is automatically set when a campaign is created from this template (if the mission is already completed).',
    },
    {
      id: 'updatedAt',
      show: showUpdatedAt && task.updatedAt,
      label: 'Updated:',
      value: task.updatedAt,
      displayValue: formatRelativeTime(task.updatedAt),
      handler: null,
      inputType: 'datetime',
      icon: ClockIcon,
      showTime: true,
      readOnly: true,
    },
  ];
}
